"use client";

import { env } from "@/env.mjs";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

interface Props {
	lang: string;
}

declare global {
	interface Window {
		lipscore?: {
			initWidgets: () => void;
			reInitWidgets: (reload: boolean) => void;
			init: (config: { apiKey: string }) => void;
		};
	}
}

export function LipscoreLoader({ lang }: Props) {
	// const enabled = useFlag("lipscore.enabled");

	const loadedLangRef = useRef<string | null>(null);

	useEffect(() => {
		if (loadedLangRef.current === lang) return;

		const existing = document.querySelector<HTMLScriptElement>("script[data-lipscore='true']");
		if (existing) {
			if (existing.dataset.lipscoreLang === lang) {
				loadedLangRef.current = lang;
				return;
			}
			// Vendor script must not run twice: swap out the stale language build before adding the new one.
			existing.remove();
		}

		window.lipscoreInit = () => {
			try {
				window.lipscore?.init({
					apiKey: env.NEXT_PUBLIC_LIPSCORE_API!,
				});
			} catch (err) {
				console.error("[Lipscore] Initialization failed:", err);
			}
		};

		const scr = document.createElement("script");

		scr.async = true;
		scr.dataset.lipscore = "true";
		scr.dataset.lipscoreLang = lang;
		scr.src = `//static.lipscore.com/assets/${lang}/lipscore-v1.js`;

		scr.onload = () => {
			document.dispatchEvent(new CustomEvent("lipscore-loaded"));
		};

		document.head.appendChild(scr);
		loadedLangRef.current = lang;
	}, [lang]);

	const pathname = usePathname();

	useEffect(() => {
		pathname;

		window.lipscore?.reInitWidgets(true);
	}, [pathname]);

	// Widget markup (recommendation carousels, cart upsell, etc.) often mounts async,
	// well after the vendor script's own initial scan — re-scan whenever it appears.
	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout> | undefined;

		const scheduleInitWidgets = () => {
			if (!document.querySelector(".lipscore-rating-small, #lipscore-rating")) return;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				window.lipscore?.initWidgets();
			}, 150);
		};

		const observer = new MutationObserver(scheduleInitWidgets);
		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			clearTimeout(timeout);
			observer.disconnect();
		};
	}, []);

	return null;
}
